'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const scheduleData = [
    { day: 'Mon', classes: 4, color: BRAND },
    { day: 'Tue', classes: 3, color: BRAND },
    { day: 'Wed', classes: 5, color: BRAND },
    { day: 'Thu', classes: 2, color: BRAND },
    { day: 'Fri', classes: 4, color: BRAND },
    { day: 'Sat', classes: 1, color: BRAND },
];

const upcomingClasses = [
    { time: '09:00 AM', subject: 'Advanced Calculus', grade: 'Grade 12', room: 'Room A3', students: 18, type: 'In-person' },
    { time: '11:00 AM', subject: 'Algebra Fundamentals', grade: 'Grade 10', room: 'Virtual', students: 24, type: 'Live' },
    { time: '02:00 PM', subject: 'Statistics & Probability', grade: 'Grade 11', room: 'Room B1', students: 22, type: 'In-person' },
    { time: '04:00 PM', subject: '1-on-1 Tutoring', grade: 'Grade 9', room: 'Virtual', students: 1, type: 'Session' },
];

export default function StudioSchedule() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="schedules" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#fff' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 90% 50%, rgba(55,87,255,0.04) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                    >
                        Schedules & Classrooms
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Your teaching week,
                            <br />
                            <span style={{ color: BRAND }}>fully organized.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Set your availability, manage class schedules, run live virtual classrooms, and let students book sessions — all from one place.
                        </p>
                    </div>
                </div>

                {/* Bento grid */}
                <div className={`grid lg:grid-cols-3 gap-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* Weekly overview — spans 2 cols */}
                    <div className="lg:col-span-2 rounded-3xl p-7" style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)' }}>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm font-800" style={{ color: '#0D0D0D' }}>Weekly Overview</p>
                            <span className="text-xs font-600 px-3 py-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>This week</span>
                        </div>
                        <div className="flex items-end gap-3 mb-6" style={{ height: '80px' }}>
                            {scheduleData.map((d) => (
                                <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                                    <div
                                        className="w-full rounded-lg transition-all duration-700"
                                        style={{
                                            height: visible ? `${(d.classes / 5) * 60}px` : '0px',
                                            background: d.day === 'Wed' ? BRAND : 'rgba(55,87,255,0.15)',
                                            transitionDelay: `${scheduleData.indexOf(d) * 80}ms`,
                                        }}
                                    />
                                    <span className="text-xs font-700" style={{ color: d.day === 'Wed' ? BRAND : '#888' }}>{d.day}</span>
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-3 gap-3 pt-4" style={{ borderTop: '1px solid rgba(55,87,255,0.08)' }}>
                            {[
                                { label: 'Classes this week', value: '19' },
                                { label: 'Total hours', value: '28h' },
                                { label: 'Students reached', value: '148' },
                            ].map(stat => (
                                <div key={stat.label}>
                                    <p className="text-xl font-800 mb-0.5" style={{ color: BRAND }}>{stat.value}</p>
                                    <p className="text-xs font-600" style={{ color: '#888' }}>{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Availability card */}
                    <div className="rounded-3xl p-7" style={{ background: BRAND }}>
                        <p className="text-xs font-700 uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em' }}>Availability</p>
                        <p className="text-sm font-800 text-white mb-5">Set your open slots for student bookings</p>
                        <div className="flex flex-col gap-2.5">
                            {[
                                { slot: 'Mon–Fri', time: '8am – 5pm', open: true },
                                { slot: 'Saturday', time: '9am – 1pm', open: true },
                                { slot: 'Sunday', time: 'Closed', open: false },
                            ].map(slot => (
                                <div key={slot.slot} className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.12)' }}>
                                    <div>
                                        <p className="text-xs font-700 text-white">{slot.slot}</p>
                                        <p className="text-xs font-500" style={{ color: 'rgba(255,255,255,0.6)' }}>{slot.time}</p>
                                    </div>
                                    <div
                                        className="w-8 h-4 rounded-full flex items-center transition-all"
                                        style={{ background: slot.open ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)', justifyContent: slot.open ? 'flex-end' : 'flex-start', padding: '2px' }}
                                    >
                                        <div className="w-3 h-3 rounded-full" style={{ background: slot.open ? BRAND : 'rgba(255,255,255,0.5)' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Today's classes */}
                    <div className="lg:col-span-3 rounded-3xl p-7" style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)' }}>
                        <p className="text-sm font-800 mb-6" style={{ color: '#0D0D0D' }}>Today's Classes</p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {upcomingClasses.map((cls, i) => (
                                <div
                                    key={cls.subject}
                                    className="rounded-2xl p-5 transition-all duration-300 cursor-pointer"
                                    style={{ background: 'white', border: '1px solid rgba(55,87,255,0.08)', transitionDelay: `${i * 60}ms` }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(55,87,255,0.1)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-700" style={{ color: '#888' }}>{cls.time}</span>
                                        <span
                                            className="text-xs font-700 px-2 py-0.5 rounded-full"
                                            style={{
                                                background: cls.type === 'Live' ? 'rgba(55,87,255,0.1)' : cls.type === 'Session' ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
                                                color: cls.type === 'Live' ? BRAND : cls.type === 'Session' ? '#F59E0B' : '#10B981',
                                            }}
                                        >
                                            {cls.type}
                                        </span>
                                    </div>
                                    <p className="text-sm font-800 mb-1" style={{ color: '#0D0D0D' }}>{cls.subject}</p>
                                    <p className="text-xs font-600 mb-3" style={{ color: '#888' }}>{cls.grade} · {cls.room}</p>
                                    <div className="flex items-center gap-1.5">
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <circle cx="6" cy="4" r="2" stroke={BRAND} strokeWidth="1.25" />
                                            <path d="M2 10c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke={BRAND} strokeWidth="1.25" strokeLinecap="round" />
                                        </svg>
                                        <span className="text-xs font-600" style={{ color: '#888' }}>{cls.students} {cls.students === 1 ? 'student' : 'students'}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
